/**
 * The buyer's view of a purchase's time, from their press of Continue to
 * the outcome on the checkout's button (LIS-1173, the purchase design's
 * §2.6 and §2.10), as Sentry metrics the server's own join by attempt id:
 * - `buy_on_behalf.client_time_to_outcome`: the press until the status
 *   shows the attempt ended, or asked to stop and not yet confirmed stopped
 *   (`stop_unconfirmed`), with its listing, its result and whether Pay was
 *   clicked;
 * - `buy_on_behalf.client_step`, by `step`: `card` (the secure fields'
 *   capture), `start` (the start request), `first_status` (the start
 *   answered until the first status read came back) and `notice` (the
 *   outcome heard until the status showed it).
 * Nothing about the buyer, their card or the seller is sent, and a failing
 * metric never touches the checkout.
 */
import type { AttemptView } from "@libs/platform-wire/purchase";
import type { AttemptMetrics, CheckoutSurface } from "../timing";
import type { AttemptRead } from "./transport";

export type PurchaseStep = "card" | "start" | "first_status" | "notice";

/** Sentry adds the scope's user to a metric unless its attributes name
 *  them: named and left empty, they are dropped as it is sent. */
const NO_USER = { "user.id": undefined, "user.email": undefined, "user.name": undefined };

/** Where the attempt has come to rest for the buyer: its outcome once
 *  ended, `stop_unconfirmed` while a stop waits to be confirmed; null while
 *  it still runs. */
export function restingResult(view: AttemptView | null): string | null {
  if (!view) return null;
  switch (view.lifecycle) {
    // While confirming the buyer may leave, but the outcome is still to come.
    case "running":
    case "stopping":
    case "confirming":
      return null;
    case "stop_unconfirmed":
      return "stop_unconfirmed";
    case "ended":
      return view.screen.kind === "outcome" ? view.screen.result : null;
  }
}

type Run = {
  pressed: number;
  card: "new" | "saved" | "free" | null;
  attempt: AttemptRead | null;
  pending: Array<[PurchaseStep, number]>;
  answered: number | null;
  firstStatus: boolean;
  heard: number | null;
};

export type PurchaseTiming = ReturnType<typeof createPurchaseTiming>;

export function createPurchaseTiming(options: {
  metrics?: AttemptMetrics | null;
  surface?: CheckoutSurface;
  /** The purchase's listing, which the metrics name in place of a seller. */
  listingId: () => string | null;
  now?: () => number;
}) {
  const now = options.now ?? (() => Date.now());
  let run: Run | null = null;
  const send = (name: string, ms: number, attributes: Record<string, string | number | boolean>) => {
    try {
      options.metrics?.distribution(name, Math.max(0, Math.round(ms)), {
        unit: "millisecond",
        attributes: {
          surface: options.surface ?? "unknown",
          listing_id: options.listingId() ?? "unknown",
          ...attributes,
          ...NO_USER,
        },
      });
    } catch {
      /* Telemetry never touches the checkout. */
    }
  };
  const step = (current: Run, name: PurchaseStep, ms: number) => {
    if (current.attempt) send("buy_on_behalf.client_step", ms, { step: name, attempt_id: current.attempt.id });
    else current.pending.push([name, ms]);
  };
  return {
    /** Continue pressed: this attempt's clock starts. */
    pressed(card: "new" | "saved" | "free" | null) {
      run = { pressed: now(), card, attempt: null, pending: [], answered: null, firstStatus: false, heard: null };
    },
    async measure<T>(name: PurchaseStep, work: () => Promise<T>): Promise<T> {
      const current = run;
      const began = now();
      try {
        return await work();
      } finally {
        if (current && current === run) step(current, name, now() - began);
      }
    },
    /** The start was answered with (or found) this attempt. */
    started(attempt: AttemptRead) {
      const current = run;
      if (!current || current.attempt) return;
      current.attempt = attempt;
      current.answered = now();
      for (const [name, ms] of current.pending.splice(0)) step(current, name, ms);
    },
    /** A status read of the attempt came back. */
    status(attempt: AttemptRead) {
      const current = run;
      if (!current || attempt.id !== current.attempt?.id) return;
      if (!current.firstStatus && current.answered !== null) {
        current.firstStatus = true;
        step(current, "first_status", now() - current.answered);
      }
      if (current.heard === null && restingResult(attempt.view) !== null) current.heard = now();
    },
    /** The status is showing the attempt as it is: its outcome, once it has
     *  come to rest. */
    shown(attempt: AttemptRead) {
      const current = run;
      if (!current || attempt.id !== current.attempt?.id) return;
      const result = restingResult(attempt.view);
      if (result === null || !attempt.view) return;
      run = null;
      const at = now();
      if (current.heard !== null) step(current, "notice", at - current.heard);
      send("buy_on_behalf.client_time_to_outcome", at - current.pressed, {
        attempt_id: attempt.id,
        result,
        pay_claimed: attempt.view.payClaimed,
        ...(current.card ? { card: current.card } : {}),
      });
    },
  };
}
